import * as React from "react";
import cx from "classnames";

import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import Dropdown from "@common/containers/fields/Dropdown";
import { trDts } from "@modules/datasheets/utils/i18n";
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";
import { ColorPicker } from "@common/components/color-picker";
import { getTestAttributes } from "@common/utils/tests";

import "./text-style-management.styles.scss";

export type TextStyleManagementProps = {
  fontFamily?: string;
  onFontFamilyChange: (fontFamily: string) => void;
  fontSize?: number;
  onFontSizeChange: (fontSize: number) => void;
  color?: string;
  onColorChange: (color: string) => void;
  textStyles?: Array<DatasheetComponentTextStyle>;
  onTextStylesChange: (styles: Array<DatasheetComponentTextStyle>) => void;
  textAlign?: DatasheetFieldComponentTextAlign;
  onTextAlignsChange?: (align: DatasheetFieldComponentTextAlign) => void;
  headingPosition?: number;
  onHeadingPositionChange?: (position: DatasheetFieldComponentHeadingPosition) => void;
  headingAlign?: DatasheetFieldComponentTextAlign;
  onHeadingAlignChange?: (align: DatasheetFieldComponentTextAlign) => void;
  textStylesOptions?: Array<{ iconClassName: string; id: string; value: DatasheetComponentTextStyle }>;
  textAlignOptions?: Array<{ iconClassName: string; id: string; value: DatasheetFieldComponentTextAlign }>;
  headingPositionOptions: Array<{ value: DatasheetFieldComponentHeadingPosition; label: string }>;
  headingAlignOptions?: Array<{ value: DatasheetFieldComponentTextAlign; label: string }>;
  fontFamilyOptions: Array<{ value: string; label: string }>;
  fontSizesOptions: Array<{ value: number; label: string }>;
  getId: {
    font: string;
    fontSize: string;
    colorPicker: string;
    positions?: string;
    aligns?: string;
  };
};

export const TextStyleManagement: React.FC<TextStyleManagementProps> = (props) => {
  const {
    fontFamily,
    color,
    headingAlign,
    headingPosition,
    fontSize,
    textAlign,
    textStyles,
    headingAlignOptions,
    headingPositionOptions,
    textStylesOptions,
    textAlignOptions,
    fontFamilyOptions,
    fontSizesOptions,
    getId,
    onFontFamilyChange,
    onColorChange,
    onHeadingAlignChange,
    onFontSizeChange,
    onHeadingPositionChange,
    onTextStylesChange,
    onTextAlignsChange,
  } = props;

  const textStylesSafe = React.useMemo<Array<DatasheetComponentTextStyle>>(
    () => {
      if (!Array.isArray(textStyles)) return [];
      // Filter to ensure only valid enum values are included
      return textStyles.filter((s): s is DatasheetComponentTextStyle => 
        s === DatasheetComponentTextStyle.bold || 
        s === DatasheetComponentTextStyle.italic || 
        s === DatasheetComponentTextStyle.underline
      );
    },
    [textStyles]
  );

  const handleTextStylesSelect = React.useCallback(
    (style: DatasheetComponentTextStyle) => {
      if (onTextStylesChange) {
        const isSelected = textStylesSafe.some((s) => s === style);
        const newStyles = isSelected
          ? textStylesSafe.filter((s) => s !== style)
          : [...textStylesSafe, style];
        onTextStylesChange(newStyles);
      }
    },
    [textStylesSafe, onTextStylesChange]
  );

  const handleTextAlignsSelect = React.useCallback(
    (style: DatasheetFieldComponentTextAlign) => {
      if (onTextAlignsChange) {
        onTextAlignsChange(style);
      }
    },
    [onTextAlignsChange]
  );

  return (
    <div>
      <div className="DatasheetsEditor__datasheet-element-settings__section">
        <Dropdown
          id={getId.font}
          options={fontFamilyOptions}
          selectedValue={fontFamily}
          searchable={false}
          onValueChanged={onFontFamilyChange}
          fullWidth
        />
      </div>
      <Flex className="DatasheetsEditor__datasheet-element-settings__section">
        <div style={{ width: '20%', minWidth: '70px' }}>
          <Dropdown
            id={getId.fontSize}
            options={fontSizesOptions}
            selectedValue={fontSize}
            searchable={false}
            onValueChanged={onFontSizeChange}
            styles={{
              control: (base: React.CSSProperties) => ({
                ...base,
                minWidth: '70px',
              }),
              singleValue: (base: React.CSSProperties) => ({
                ...base,
                overflow: 'visible',
                textOverflow: 'unset',
              }),
            }}
          />
        </div>
        <div style={{ marginLeft: "4px" }}>
          <ColorPicker 
            inputProps={getTestAttributes({ id: getId.colorPicker})} 
            buttonProps={getTestAttributes({ id: "fp-datasheet-design-right-panel-datacolorbtn" })}
            onChange={onColorChange} 
            value={color} 
          />
        </div>
        {onTextAlignsChange && textAlign !== undefined && headingAlignOptions && (
          <Flex alignItems="center" style={{ marginLeft: "4px", flex: 1 }}>
            <span style={{ marginRight: "4px" }}>{trDts("Alignment")}</span>
            <Dropdown
              id={getId.aligns}
              options={headingAlignOptions}
              fullWidth
              selectedValue={textAlign}
              searchable={false}
              onValueChanged={onTextAlignsChange}
            />
          </Flex>
        )}
      </Flex>
      <Flex className="DatasheetsEditor__datasheet-element-settings__section">
        {textStylesOptions?.map((btn) => (
          <Button
            key={btn.id}
            id={btn.id}
            style={{ marginLeft: "4px", width: "32px", height: "32px", textOverflow: "unset", padding: 0 }}
            className={cx({
              ["DatasheetsEditor__datasheet-element-settings__text-style--selected"]: textStylesSafe.includes(
                btn.value
              ),
            })}
            onClick={() => {
              handleTextStylesSelect(btn.value);
            }}
            bsStyle="primary"
          >
            <span className={btn.iconClassName} />
          </Button>
        ))}
      </Flex>
      {onHeadingPositionChange && (
        <Flex alignItems="center" className="DatasheetsEditor__datasheet-element-settings__section">
          <span style={{ marginRight: "4px" }}>{trDts("Position")}</span>
          <Dropdown
            id={getId.positions}
            options={headingPositionOptions}
            fullWidth
            selectedValue={headingPosition}
            searchable={false}
            onValueChanged={onHeadingPositionChange}
          />
        </Flex>
      )}
      {onHeadingAlignChange && (
        <Flex alignItems="center" className="DatasheetsEditor__datasheet-element-settings__section">
          <span style={{ marginRight: "4px" }}>{trDts("Alignment")}</span>
          <Dropdown
            id={getId.aligns}
            options={headingAlignOptions}
            fullWidth
            selectedValue={headingAlign}
            searchable={false}
            onValueChanged={onHeadingAlignChange}
          />
        </Flex>
      )}
      {textAlignOptions && !headingAlignOptions && (
        <Flex className="DatasheetsEditor__datasheet-element-settings__section">
          {textAlignOptions.map((btn) => (
            <Button
              key={btn.id}
              id={btn.id}
              style={{ marginLeft: "4px", width: "32px", height: "32px", textOverflow: "unset", padding: 0 }}
              className={cx({
                ["DatasheetsEditor__datasheet-element-settings__text-style--selected"]: textAlign === btn.value,
              })}
              onClick={() => {
                handleTextAlignsSelect(btn.value);
              }}
              bsStyle="primary"
            >
              <span className={btn.iconClassName} />
            </Button>
          ))}
        </Flex>
      )}
    </div>
  );
};
